import type { Selection } from '@tiptap/pm/state';
import { ETiptapSelectorKey } from '../types/tiptap.types';

export const LIST_TYPES = new Set<string>([
  ETiptapSelectorKey.BULLET_LIST,
  ETiptapSelectorKey.ORDERED_LIST_WITH_START,
  ETiptapSelectorKey.TASK_LIST,
]);

/**
 * Expand special selections to their full container boundaries.
 *
 * - **CellSelection** → full table (ProseMirror's CellSelection points at
 *   individual cells, not the enclosing table).
 * - **List selection** → outermost list (partial list wrapping creates invalid
 *   structure because listItem nodes can't contain block fields).
 *
 * For other selections the original from/to are returned unchanged.
 */
const expandCellSelection = (selection: Selection): { from: number; to: number } => {
  let { from, to } = selection;

  // CellSelection → full table
  if ('$anchorCell' in selection) {
    const $f = selection.$from.doc.resolve(from);
    for (let d = $f.depth; d >= 0; d--) {
      if ($f.node(d).type.name === 'table') {
        from = $f.before(d);
        to = $f.after(d);
        break;
      }
    }
    return { from, to };
  }

  // List selection → outermost list
  const { $from, $to } = selection;
  let listDepth = -1;
  for (let d = $from.depth; d >= 0; d--) {
    if (LIST_TYPES.has($from.node(d).type.name)) listDepth = d;
  }
  if (listDepth >= 0) {
    // Verify $to is also inside this same list node
    if ($to.depth >= listDepth && $to.node(listDepth) === $from.node(listDepth)) {
      from = $from.before(listDepth);
      to = $from.after(listDepth);
    }
  }

  return { from, to };
};

export default expandCellSelection;
